import { CardContent, Typography, Box, Skeleton } from '@mui/material';
import { EmailContentSection } from './EmailContentSection';
import { Visibility, Edit, Save } from '@mui/icons-material';
import { PreviewCard as PreviewCardComponent, ActionButtons, ActionButton, SubjectLine, EmailBody } from './StyledComponents';
// import WheelerWatingMAIQ from '../../../../../../components/WheelerWatingMAIQ'; // Removed - using GenerationProgressPopupMAIQ instead
import { GenerationProgressPopupMAIQ } from 'app/components/GenerationProgressPopupMAIQ';

export const PreviewCard = ({
  emailContent,
  isGenerating,
  isGeneratingEmail,
  generatedEmail,
  onPreview,
  handleEditClick,
  handleSaveTemplateClick,
  streamingData = null,
  streamingText = '',
  currentSection = null,
  showActionButtons = true,
}: {
  emailContent: any;
  isGenerating: boolean;
  isGeneratingEmail: boolean;
  generatedEmail: any;
  onPreview: () => void;
  handleEditClick: () => void;
  handleSaveTemplateClick: () => void;
  streamingData?: any;
  streamingText?: string;
  currentSection?: {index: number; type: string} | null | undefined;
  showActionButtons?: boolean;
}) => {
  
  return (
    <>
      {/* Campaign Progress Style Cards */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        // p: 2,
        width: '100%',
      }}>
        {/* Show skeleton when generating */}
        {isGenerating ? (
          <Box sx={{ 
            py: 4,
            px: 3,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Skeleton for Subject */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} />
            </Box>
            
            {/* Skeleton for Opening Message */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="25%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="95%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            
            {/* Skeleton for Additional Sections */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="85%" height={20} />
            </Box>
            
            {/* Skeleton for Service Section */}
            <Box>
              <Skeleton variant="text" width="15%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Box>
        ) : (
          <>
            {/* Subject Card */}
            {emailContent.subject && (
          <Box sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'primary.50',
            borderRadius: 2,
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.secondary',
                mb: 0.5,
                fontFamily: 'inherit'
              }}
            >
              Subject
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                fontFamily: 'inherit'
              }}
            >
              {emailContent.subject}
            </Typography>
          </Box>
        )}

        {/* Email Content Cards */}
        {(isGeneratingEmail || (!emailContent.sections || emailContent.sections.length === 0)) ? (
          <Box sx={{ 
            py: 4,
            px: 3,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Skeleton for Subject */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="20%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} />
            </Box>
            
            {/* Skeleton for Opening Message */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="25%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="95%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
            
            {/* Skeleton for Additional Sections */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="85%" height={20} />
            </Box>
            
            {/* Skeleton for Service Section */}
            <Box>
              <Skeleton variant="text" width="15%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
          </Box>
        ) : (
          emailContent.sections && emailContent.sections
            .filter((el) => {
              // Only show sections that have a title/type
              return el.type || el.title || el.name;
            })
            .map((el, index) => {
              // Different colors for different sections - theme compatible
              const getSectionColor = (theme: any, index: number) => {
                if (theme.palette.mode === 'dark') {
                  const darkColors = [
                    'rgba(76, 175, 80, 0.2)',   // Green
                    'rgba(156, 39, 176, 0.2)',  // Purple  
                    'rgba(255, 193, 7, 0.2)',   // Yellow
                    'rgba(255, 87, 34, 0.2)',   // Orange
                    'rgba(33, 150, 243, 0.2)'   // Blue
                  ];
                  return darkColors[index % darkColors.length];
                } else {
                  const lightColors = [
                    'rgba(76, 175, 80, 0.1)',   // Green
                    'rgba(156, 39, 176, 0.1)',  // Purple  
                    'rgba(255, 193, 7, 0.1)',   // Yellow
                    'rgba(255, 87, 34, 0.1)',   // Orange
                    'rgba(33, 150, 243, 0.1)'   // Blue
                  ];
                  return lightColors[index % lightColors.length];
                }
              };
              
              // Get the title for this section
              const sectionTitle = el.type || el.title || el.name || `Section ${index + 1}`;
              
              // Get the content for this section
              const sectionContent = el.content || el.text || el.description || 'Content loading...';
              
              
              return (
                <Box key={el.id} sx={{
                  backgroundColor: (theme) => getSectionColor(theme, index),
                  borderRadius: 2,
                  p: 2.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                      mb: 0.5,
                      fontFamily: 'inherit'
                    }}
                  >
                    {sectionTitle}
                  </Typography>
                  
                  {/* Check if content is an image URL or base64 data */}
                  {(() => {
                    // Check all possible image sources
                    const hasImageField = !!(el.image || el.imageUrl || el.img || el.photo || el.picture || el.img_url);
                    const isImageUrl = sectionContent.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);
                    const isDataImage = sectionContent.startsWith('data:image/');
                    const isHttpUrl = sectionContent.startsWith('http');
                    
                    return hasImageField || isImageUrl || isDataImage || isHttpUrl;
                  })() ? (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      mt: 1
                    }}>
                      <img 
                        src={el.image || el.imageUrl || el.img || el.photo || el.picture || el.img_url || sectionContent} 
                        alt={sectionTitle}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          // Fallback to text if image fails to load
                          e.currentTarget.style.display = 'none';
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.textContent = sectionContent;
                          fallbackDiv.style.fontSize = '0.875rem';
                          fallbackDiv.style.color = 'inherit';
                          fallbackDiv.style.fontFamily = 'inherit';
                          e.currentTarget.parentNode?.appendChild(fallbackDiv);
                        }}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          color: 'text.primary',
                          fontFamily: 'inherit'
                        }}
                      >
                        {sectionContent}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })
        )}

        {/* Generated Email Card */}
        {generatedEmail && (
          <Box sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'info.50',
            borderRadius: 2,
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.secondary',
                mb: 0.5,
                fontFamily: 'inherit'
              }}
            >
              Generated Email
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '0.875rem',
                fontWeight: 400,
                color: 'text.primary',
                fontFamily: 'inherit'
              }}
            >
              Email ready for preview
            </Typography>
          </Box>
        )}

            {/* Action Buttons Card */}
            {showActionButtons && (
              <Box sx={{
                backgroundColor: '#e8f4fd',
                borderRadius: 2,
                p: 2.5,
                border: '1px solid #e0e0e0'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#666666',
                    mb: 1,
                    fontFamily: 'inherit'
                  }}
                >
                  Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    component="button"
                    onClick={onPreview}
                    disabled={isGenerating || !generatedEmail}
                    sx={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      p: 1,
                      cursor: (isGenerating || !generatedEmail) ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: 'inherit',
                      color: (isGenerating || !generatedEmail) ? '#999' : '#333',
                      opacity: (isGenerating || !generatedEmail) ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: (isGenerating || !generatedEmail) ? 'transparent' : '#f5f5f5'
                      }
                    }}
                  >
                    {generatedEmail ? 'View Generated Email' : 'Preview'}
                  </Box>
                  <Box
                    component="button"
                    onClick={handleEditClick}
                    disabled={isGenerating || !generatedEmail}
                    sx={{
                      backgroundColor: 'transparent',
                      border: '1px solid #ccc',
                      borderRadius: 1,
                      p: 1,
                      cursor: (isGenerating || !generatedEmail) ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: 'inherit',
                      color: (isGenerating || !generatedEmail) ? '#999' : '#333',
                      opacity: (isGenerating || !generatedEmail) ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: (isGenerating || !generatedEmail) ? 'transparent' : '#f5f5f5'
                      }
                    }}
                  >
                    Edit
                  </Box>
                  <Box
                    component="button"
                    onClick={handleSaveTemplateClick}
                    disabled={isGenerating || !generatedEmail}
                    sx={{
                      backgroundColor: (isGenerating || !generatedEmail) ? '#ccc' : '#333',
                      border: 'none',
                      borderRadius: 1,
                      p: 1,
                      cursor: (isGenerating || !generatedEmail) ? 'not-allowed' : 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: 'inherit',
                      color: 'white',
                      opacity: (isGenerating || !generatedEmail) ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: (isGenerating || !generatedEmail) ? '#ccc' : '#555'
                      }
                    }}
                  >
                    Save Template
                  </Box>
                </Box>

                {/* Show message when buttons are disabled */}
                {!generatedEmail && !isGenerating && (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 2, 
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    fontSize: '0.75rem',
                    mt: 1
                  }}>
                    Complete the chat flow to generate email and enable these actions
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* NEW: Generation Progress Popup - Shows detailed progress in popup */}
      {isGeneratingEmail && (
        <GenerationProgressPopupMAIQ
          open={isGeneratingEmail}
          isGenerating={isGeneratingEmail}
          streamingData={streamingData}
          streamingText={streamingText}
          currentSection={currentSection || undefined}
          sections={streamingData?.message?.sections || []}
          isFinal={streamingData?.message?.isFinal || false}
          showProgress={true}
          message="Generating Email..."
        />
      )}
    </>
  );
}